import { Alert, Button, Badge } from "@/components/ui";
import * as React from "react";
import { useEffect, useRef, useState, useMemo } from "react";
import { get } from "lodash";
import { useAccountSettings, removeWhiteSpace, useComplianceChecker, useSocialCoachSession } from "@/utils";
import { HashTagElement, TagElement, WebComponents } from "@/interfaces";
import { HashTagsServices } from "@/services";
import { ConfigContext } from "@/contexts/appContexts";
import { AlertCircleIcon, XIcon } from "lucide-react";
import { toast } from "sonner";
import { FormCard } from "@/components/FormCard";
import { cn } from "@/lib/utils";

const MAX_HASHTAGS = 30;

interface Props {
  playerId: number;
}

export function PlayerHashTagsForm({ playerId }: Props) {
  const { coachId, isPlayer } = useSocialCoachSession();
  const { blockedPhrases } = useComplianceChecker({ playerId, coachId });
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = isPlayer || hasWritePermission(WebComponents.PLAYERS);

  const config = React.useContext(ConfigContext);
  const keywordMaxLength = get(config, "keywordMaxLength", 30);

  const [updatedHashTagsList, setUpdatedHashTagsList] = useState<TagElement[]>([]);
  const [hashtagsListError, setHashtagsListError] = useState<{ [key: string]: string } | undefined>();
  const [saving, setSaving] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const tagCounterRef = useRef<number>(0);

  const hashTagsAsTags = (result: HashTagElement[]): TagElement[] => {
    tagCounterRef.current = result.length;
    return result.map((hashtag, index) => ({
      index,
      displayValue: hashtag.content,
      id: `${index}`,
    }));
  };

  useEffect(() => {
    if (playerId) {
      HashTagsServices.get(playerId).then(response => {
        const playerHashTags = hashTagsAsTags(response);
        setUpdatedHashTagsList(playerHashTags);
      });
    }
  }, [playerId]);

  const saveTags = async () => {
    setSaving(true);
    const toUpdate = updatedHashTagsList.map(hashtag => hashtag.displayValue);
    await HashTagsServices.update(playerId, {
      hashtags: toUpdate,
      addHashTagAutomatically: true,
    });
    toast.success("Hashtags saved!");
    setSaving(false);
  };

  const setErrors = (errorKeywords: string[]): { [key: string]: string } | undefined => {
    let newErrors = {};
    errorKeywords.map((error: string) => {
      newErrors = {
        ...newErrors,
        [error]: error,
      };
    });

    return newErrors;
  };

  const addTags = (newTags: string[]) => {
    setUpdatedHashTagsList(selected => {
      return selected.concat(
        newTags.map(newTag => ({
          index: tagCounterRef.current++,
          displayValue: removeWhiteSpace(newTag).replace(/#/g, ""),
        }))
      );
    });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const getHashtagError = (newValue: string, currentTags: TagElement[]): string | null => {
    const currentHashTags = currentTags.map(a => a.displayValue.toLowerCase());
    const valueToCompare = newValue.toLowerCase().replace(/#/g, "");
    if (currentHashTags.includes(valueToCompare)) {
      return `The hashtag '${newValue}' has already been used.`;
    } else if (blockedPhrases.includes(valueToCompare)) {
      return `The hashtag '${newValue}' is not allowed.`;
    }
    return null;
  };

  const onNewInput = (newInputValue: string): void => {
    if (withWritePermission && newInputValue.length > 0) {
      const inputValues = newInputValue.split(/[ ,]/);

      let errors: string[] = [];
      if (updatedHashTagsList.length + inputValues.length > MAX_HASHTAGS) {
        errors = ["You can add a maximum of 30 hashtags"];
      } else {
        const tagsToAdd: string[] = [];
        inputValues.forEach(value => {
          const inputValue = removeWhiteSpace(value).replace(/#/g, "");
          const notAddedSoFar = tagsToAdd.indexOf(inputValue) === -1;
          const inputNotEmpty = inputValue !== "";
          if (inputNotEmpty && notAddedSoFar) {
            const hashtagError = getHashtagError(inputValue, updatedHashTagsList);
            if (!hashtagError) {
              tagsToAdd.push(inputValue);
            } else if (errors.indexOf(hashtagError) === -1) {
              errors.push(hashtagError);
            }
          }
        });
        if (tagsToAdd.length > 0) {
          addTags(tagsToAdd);
        }
      }
      if (errors.length > 0) {
        setHashtagsListError(setErrors(errors));
      } else {
        setHashtagsListError(undefined);
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (withWritePermission) {
      if (e.key === "Enter") {
        e.preventDefault();
        const inputValue = e.currentTarget.value || "";
        onNewInput(inputValue);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (withWritePermission) {
      const deleteLastTag = () => {
        setUpdatedHashTagsList(oldTags => {
          const nextTags = [...oldTags];
          nextTags.pop();
          return nextTags;
        });
      };
      if (e.key === "Enter") {
        e.preventDefault();
      }
      if (e.key === "Backspace" && e.currentTarget.selectionStart === 0) {
        deleteLastTag();
      } else if (e.key !== "Backspace" && e.currentTarget.value.length >= keywordMaxLength) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    onNewInput(e.clipboardData.getData("Text"));
  };

  const removeTag = (index: number): void => {
    if (withWritePermission) {
      setUpdatedHashTagsList(oldTags => oldTags.filter(tag => tag.index !== index));
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const displayedErrors = useMemo(() => {
    const errorList: string[] = [];
    if (hashtagsListError) {
      Object.keys(hashtagsListError).map(key => {
        return (
          hashtagsListError[key] &&
          !errorList.includes(hashtagsListError[key]) &&
          errorList.push(hashtagsListError[key])
        );
      });
    }
    return errorList;
  }, [hashtagsListError]);

  return (
    <FormCard>
      <FormCard.Header title="Hashtags" description="Automatically appended to eligible post captions" />
      <FormCard.Content>
        {displayedErrors.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {displayedErrors.map(error => (
              <Alert key={error} variant="destructive">
                <AlertCircleIcon className="size-4" />
                <Alert.Title>Error</Alert.Title>
                <Alert.Description>{error}</Alert.Description>
              </Alert>
            ))}
          </div>
        )}
        <div
          onClick={focusInput}
          className={cn(
            "flex flex-col gap-2 w-full flex-1 min-h-[111px] h-full overflow-y-auto bg-white border border-slate-200 rounded-lg p-3 cursor-text font-sans font-normal text-slate-700 outline-none focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-3 transition-all",
            !withWritePermission && "opacity-50 pointer-events-none"
          )}
        >
          {updatedHashTagsList.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {updatedHashTagsList.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-sm px-2 py-0.5 inline-flex items-center gap-1">
                  {tag && tag.displayValue}
                  {withWritePermission && (
                    <XIcon
                      size={13}
                      onClick={e => {
                        e.stopPropagation();
                        removeTag(tag.index);
                      }}
                      className="cursor-pointer hover:text-slate-900 transition-colors"
                    />
                  )}
                </Badge>
              ))}
            </div>
          )}
          <input
            className="w-full bg-transparent border-none outline-none focus:ring-0 text-base placeholder:italic placeholder:font-light placeholder:text-slate-400"
            ref={inputRef}
            placeholder={updatedHashTagsList.length > 0 ? undefined : "Type some hashtags here..."}
            type="text"
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            disabled={!withWritePermission}
            onPaste={handlePaste}
          />
        </div>
      </FormCard.Content>
      <FormCard.Actions>
        <Button onClick={saveTags} disabled={saving} loading={saving}>
          Save
        </Button>
      </FormCard.Actions>
    </FormCard>
  );
}
